import {
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  Users,
  Tag,
  Paperclip,
  MessageCircle,
  CircleDot,
  AlertCircle,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { UserMultiSelectList } from "@/components/ui/user-multi-select";
import { DisciplineMultiSelectList } from "@/domains/pm/components/shared/DisciplineMultiSelectList";
import { StatusMultiSelectList } from "@/domains/pm/components/shared/StatusMultiSelectList";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { doneStatusKeys } from "@/domains/pm/statuses/statusCatalogue";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { CONTROL_ACTIVE_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import type { IssueFilterDimension, IssueFilterValue } from "./issueFilters";
import type { Discipline, IssueStatus, UserProfile } from "@/domains/pm/types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

type Dimension = IssueFilterDimension;

// Status leads — it is the attribute users reach for first, and it led the
// header strip these dimensions replaced.
//
// The list splits in two. The first three pick from a set and need a panel;
// the last three are single yes/no predicates, so they are switches sitting
// directly in the list — a drill-down to flip one flag was two clicks and a
// pane for something the row can say by itself. Mirrors "Verified only" on
// the documents filter, which is the same shape.
const PANEL_DIMENSIONS: Dimension[] = ["status", "assignee", "discipline"];
const SWITCH_DIMENSIONS: Dimension[] = ["documents", "comments", "overdue"];
const DIMENSIONS: Dimension[] = [...PANEL_DIMENSIONS, ...SWITCH_DIMENSIONS];

// Attribute icons — matched to the issue context menu / Group By list.
const DIMENSION_ICON: Record<Dimension, LucideIcon> = {
  status: CircleDot,
  overdue: AlertCircle,
  assignee: Users,
  discipline: Tag,
  documents: Paperclip,
  comments: MessageCircle,
};

interface IssueFilterControlProps {
  users: UserProfile[];
  disciplines: Discipline[];
  value: IssueFilterValue;
  onChange: (next: IssueFilterValue) => void;
  /**
   * Dimensions this host does not offer. "My Issues" hides `assignee` (its
   * tabs already lock the person-relationship) and `overdue` (it owns that
   * predicate through its own Overdue tab); two controls for one predicate
   * would fight. Generalised from the original `hideAssignee`.
   */
  hiddenDimensions?: Dimension[];
  /** Scope-wide per-status totals shown against the status rows. */
  statusCounts?: Record<IssueStatus, number>;
  /** The project in scope, or null for "All projects". Decides which custom
   *  statuses appear. */
  buildingProjectId?: number | null;
  /** Opens the "New status" dialog. Supplied only when the page has a single
   *  project in scope and the user may manage it — the permission check and
   *  the dialogs live with the page, so this control stays renderable without
   *  a store provider. */
  onCreateStatus?: () => void;
  /** Scope-wide overdue total. Rendered red when non-zero — it is a warning,
   *  and it used to be a permanent red badge in the page header. */
  overdueCount?: number;
  /** How many dimensions are filtered — drives the badge and the active look. */
  activeCount: number;
  /** Popover state is owned by the toolbar so a chip click can reopen its dimension. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: Dimension | null;
  onStepChange: (step: Dimension | null) => void;
}

/**
 * Cascading filter control for the PM issues list. A "Filters" button sits in
 * the toolbar's right-hand cluster; opening it lists the filter dimensions, and
 * picking one swaps the popover to that dimension's value picker. The button
 * carries a count badge of the active dimensions; the chips themselves live in
 * `IssueFilterChips`, to the left of the cluster.
 *
 * The badge appears only once something is filtered — no reserved slot, so the
 * unfiltered button is just "Filters" with no dead space on its right. The
 * width it gains when the badge appears is taken from the chip row to its left
 * (the cluster is right-pinned), so the search box never moves.
 */
export function IssueFilterControl({
  users,
  disciplines,
  value,
  onChange,
  hiddenDimensions = [],
  statusCounts,
  buildingProjectId,
  onCreateStatus,
  overdueCount = 0,
  activeCount,
  open,
  onOpenChange,
  step: stepProp,
  onStepChange,
}: IssueFilterControlProps) {
  const { t } = useLanguage();
  const { catalogue: statusCatalogue } = useStatusCatalogue();
  const f = t.pm.issues.filterBy;
  const setOpen = onOpenChange;
  const setStep = onStepChange;

  // `step` is caller-controlled, so the picker must not trust it. A hidden
  // dimension has no business showing a panel, and a switch dimension no
  // longer HAS one — a chip click routes here for both, and either would
  // otherwise render an empty popover.
  const step =
    stepProp && !hiddenDimensions.includes(stepProp) && PANEL_DIMENSIONS.includes(stepProp)
      ? stepProp
      : null;

  const dimensions = DIMENSIONS.filter((d) => !hiddenDimensions.includes(d));

  // A switch row is labelled by what switching it ON does, not by the
  // attribute it filters — "Has comments" rather than "Comments", which on a
  // toggle would read as a category with no stated direction.
  const dimensionLabel = (d: Dimension): string =>
    d === "status"
      ? f.status
      : d === "overdue"
        ? f.overdueOnly
        : d === "assignee"
          ? f.assignee
          : d === "discipline"
            ? f.discipline
            : d === "documents"
              ? f.hasDocuments
              : f.hasComments;

  /**
   * Overdue pins status to in_progress at QUERY time (`toPMIssueFilters`), so
   * this only flips the flag — it must NOT prune `value.status`. Pruning made
   * the toggle one-way: switching overdue back off could not restore statuses
   * the toggle had already deleted. The pinning stays visible instead: the
   * status picker disables the two statuses overdue rules out and says why,
   * and the chips drop the status dimension while overdue is on so nothing
   * advertises a selection the query is overriding.
   */
  const pickOverdue = (next: boolean) => onChange({ ...value, overdueOnly: next || undefined });

  /**
   * The switch dimensions set the affirmative predicate or clear it —
   * `undefined`, never `false`. The repository still understands `false`
   * ("issues with NO linked documents"); the UI simply no longer offers it,
   * because a switch has nowhere to put a third state and the negative was
   * the rarely-wanted half.
   */
  const setFlag = (key: "hasLinkedDocuments" | "hasComments", on: boolean) =>
    onChange({ ...value, [key]: on || undefined });

  const switchState = (d: Dimension): { on: boolean; toggle: (on: boolean) => void } =>
    d === "documents"
      ? { on: value.hasLinkedDocuments === true, toggle: (on) => setFlag("hasLinkedDocuments", on) }
      : d === "comments"
        ? { on: value.hasComments === true, toggle: (on) => setFlag("hasComments", on) }
        : { on: value.overdueOnly === true, toggle: pickOverdue };

  const stepHeader = (d: Dimension) => {
    const Icon = DIMENSION_ICON[d];
    return (
      <div className="flex items-center gap-1.5 border-b px-1 py-1">
        <button
          type="button"
          onClick={() => setStep(null)}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Back"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium">{dimensionLabel(d)}</span>
      </div>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setStep(null);
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="control"
          aria-label={activeCount > 0 ? `${f.title} (${activeCount})` : f.title}
          className={cn("gap-1.5", activeCount > 0 && CONTROL_ACTIVE_CLASS)}
          {...pageInfoTarget("issues.filters")}
        >
          <SlidersHorizontal className="opacity-60" />
          {f.title}
          {activeCount > 0 && (
            <span
              aria-hidden="true"
              className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground"
            >
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        {step === null && (
          <div className="p-1">
            {dimensions.map((d) => {
              const Icon = DIMENSION_ICON[d];
              const overdueBadge = d === "overdue" && overdueCount > 0 && (
                // The overdue total used to be a permanent red badge in the
                // page header. It follows the dimension here so the warning is
                // not lost, red only when there is one.
                <span className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[#fcebeb] px-1 text-[10px] font-semibold tabular-nums text-[#ef4129]">
                  {overdueCount}
                </span>
              );

              if (SWITCH_DIMENSIONS.includes(d)) {
                const { on, toggle } = switchState(d);
                return (
                  // A label, not a button: the whole row is the switch's hit
                  // area, so the click target matches what it looks like.
                  <label
                    key={d}
                    className="flex w-full cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-xs hover:bg-muted"
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      {dimensionLabel(d)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {overdueBadge}
                      <Switch
                        checked={on}
                        onCheckedChange={toggle}
                        aria-label={dimensionLabel(d)}
                        className="origin-right scale-[0.7]"
                      />
                    </span>
                  </label>
                );
              }

              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setStep(d)}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    {dimensionLabel(d)}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}

        {step === "status" && (
          <div>
            {stepHeader("status")}
            <div className="p-1">
              <StatusMultiSelectList
                selectedStatuses={value.status}
                onChange={(next) => onChange({ ...value, status: next })}
                // Overdue implies open work, so every FINISHED status is shown
                // disabled and the pinning explains itself. Read from the
                // catalogue rather than the literal resolved/closed pair: a
                // project's own "Cancelled" is finished too.
                disabledStatuses={
                  value.overdueOnly ? doneStatusKeys(statusCatalogue, buildingProjectId) : []
                }
                note={value.overdueOnly ? f.overduePinsStatus : undefined}
                counts={statusCounts}
                buildingProjectId={buildingProjectId}
                footer={
                  onCreateStatus ? (
                    <button
                      type="button"
                      onClick={onCreateStatus}
                      className="mt-1 flex w-full items-center gap-2 rounded border-t px-1.5 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t.pm.issues.statuses.newStatus}
                    </button>
                  ) : buildingProjectId == null ? (
                    <p className="border-t px-1.5 pt-1.5 text-[10px] leading-snug text-muted-foreground">
                      {t.pm.issues.statuses.needsProject}
                    </p>
                  ) : null
                }
              />
            </div>
          </div>
        )}

        {step === "assignee" && (
          <div>
            {stepHeader("assignee")}
            {/* Reuse the shared user checklist so the filter looks
                  identical to every issue assignee editor. */}
            <UserMultiSelectList
              users={users}
              selectedUserIds={value.assigneeUserIds}
              onChange={(ids) => onChange({ ...value, assigneeUserIds: ids })}
              searchPlaceholder={t.pm.issues.searchUsers}
              emptyMessage={t.pm.issues.noUsersFound}
            />
          </div>
        )}

        {step === "discipline" && (
          <div>
            {stepHeader("discipline")}
            {/* WHA-3449: reuse the shared discipline checklist so the
                  filter looks identical to every issue discipline editor. */}
            <div className="p-1">
              <DisciplineMultiSelectList
                disciplines={disciplines}
                selectedIds={value.disciplineIds}
                onChange={(ids) => onChange({ ...value, disciplineIds: ids })}
                emptyMessage={t.pm.issues.noDiscipline}
              />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
